import getNextConfig from '@maxpreps/shared/constants/getNextConfig';
import useHasMounted from '@maxpreps/shared/hooks/useHasMounted';
import {
  DEFAULT_STATUS_CODE,
  useStatusCode
} from '@maxpreps/shared/hooks/useStatusCode';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect } from 'react';
import useIsAdmin from '../../hooks/useIsAdmin';
import { initialPageViewGuid } from '../../hooks/usePageViewGuid';
import { IWindow } from '../../models/IWindow';
import { getUTagObject } from './tealiumHelpers';

declare const window: IWindow;

const INTERNAL_DOMAIN_ = '.maxpreps.com';

if (typeof window !== 'undefined') {
  // update dom on initial run with only variable that can't be generated on the server
  Object.assign(window.utag_data ?? {}, {
    pageViewGuid: initialPageViewGuid
  });
}

const { appEnv } = getNextConfig().publicRuntimeConfig;

const TRACKING_ENABLED_QUERY_PARAM_VALUE = '1';

export const Tealium = ({
  projectMetaData,
  pageMetaData,
  contextDataPoints,
  deviceType,
  pageViewGuid,
  initialStatusCode = DEFAULT_STATUS_CODE
}) => {
  const isAdmin = useIsAdmin();
  const router = useRouter();
  const hasMounted = useHasMounted();

  // used to track when to increment pvs
  const [statusCode, setStatusCode] = useStatusCode(
    initialStatusCode === DEFAULT_STATUS_CODE ? undefined : initialStatusCode
  );

  // hook when routes change to actually toggle a pv.
  useEffect(() => {
    if (!statusCode) {
      return;
    }

    const newUtagData = getUTagObject(
      window.utag_data,
      projectMetaData,
      pageMetaData,
      contextDataPoints,
      deviceType,
      pageViewGuid,
      isAdmin,
      statusCode
    );

    // SSR pageview tracking is handled internally by tealium
    // This means we only need to worry about CSR tracking
    if (hasMounted) {
      try {
        // update dom
        Object.assign(window.utag_data, newUtagData);
        // pass new object to this element
        // https://docs.tealium.com/platforms/javascript/track/
        window.utag && window.utag.view(newUtagData);

        // console.log(
        //   'Tealium',
        //   'window.utag.view',
        //   pageViewGuid,
        //   newUtagData.pageUrl
        // );
      } catch (error) {
        // maybe should log to sentry?
        console.log('Tealium Error', error);
      }
    }

    setStatusCode(undefined);
  }, [
    statusCode,
    setStatusCode,
    pageViewGuid,
    deviceType,
    isAdmin,
    projectMetaData,
    pageMetaData,
    contextDataPoints,
    hasMounted
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const clickHandler = (e: MouseEvent) => {
      var element = (e.target instanceof HTMLElement &&
        e.target?.closest('a')) as HTMLAnchorElement;

      var hostname = element?.hostname;

      // don't track if it's internal to
      // EXCEPT for support.maxpreps.com
      if (
        (hostname?.endsWith(INTERNAL_DOMAIN_) &&
          !hostname?.includes('support' + INTERNAL_DOMAIN_)) ??
        true
      ) {
        return;
      }

      try {
        window.om.trackAction(
          'external link click',
          // destructure to get a few props
          (({
            pageViewGuid,
            siteSection,
            pageName,
            siteHier,
            externalLinkURL = element.href
          }) => ({
            pageViewGuid,
            siteSection,
            pageName,
            siteHier,
            externalLinkURL
          }))(window.utag_data)
        );
      } catch (error) {
        // fail silently
      }
    };

    document.body.addEventListener('click', clickHandler, {});

    return () => {
      document.body.removeEventListener('click', clickHandler, {});
    };
  }, []);

  const isEnabled =
    (router.query?.trackingenabled ?? TRACKING_ENABLED_QUERY_PARAM_VALUE) ===
    TRACKING_ENABLED_QUERY_PARAM_VALUE;

  const initialGet = () =>
    getUTagObject(
      (typeof window !== 'undefined' && window.utag_data) || {},
      projectMetaData,
      pageMetaData,
      contextDataPoints,
      deviceType,
      initialPageViewGuid,
      isAdmin,
      statusCode
    );

  const tealiumEnvironment =
    appEnv?.startsWith('dev') ?? false // dev or development
      ? 'dev'
      : appEnv === 'staging'
      ? 'qa'
      : 'prod';

  /*
   * - Coded in 2021
   * - Review in 2022 - Looking at the execution order, there are some quirks
   *   to this that weren't initial known. The initialGet is called on the server
   *   and on the client during hydration. I thought the server would output the
   *   raw response and not include a hydration initialGet in the _app.js file.
   *   The result is the utag_data object is seeded with server data. It's overriden
   *   with hydration data then overriden when the tealium script loads.
   */
  return (
    <>
      <Head>
        <script
          key="tealium-init"
          dangerouslySetInnerHTML={{
            __html: `
              var utag_data = utag_data || ${JSON.stringify(initialGet())};
            utag_data.pageUrl = window.location.href;`
          }}
        />
      </Head>
      {isEnabled && (
        <Script
          id="tealium-lib"
          src={`//tags.tiqcdn.com/utag/cbsi/maxprepssite/${tealiumEnvironment}/utag.js`}
          strategy="lazyOnload"
          onLoad={() => {
            window.utag_data = Object.assign(
              window.utag_data || {},
              initialGet()
            );
          }}
        />
      )}
    </>
  );
};
